"use client";
/**
 * This context provider should be of general use, not specific (e.g. to only commerce)
 * Opening modals, sidebars etc. is handled here, the content of it should be
 * passed/configured via specific packages e.g. commerce
 */

import { INotification } from "./interfaces/notification";
import {
  TagEntity,
  Breadcrumb,
  CommerceEntity,
  Commerce,
} from "./graphql/generated/graphql";

import {
  useMemo,
  useCallback,
  useReducer,
  createContext,
  useContext,
} from "react";
import { EventBus, Events } from "./utils/tracker";
import { ISiteConfig } from "./interfaces/site-config";

interface IState {
  displaySidebar: boolean;
  displayDropdown: boolean;
  displayModal: boolean;
  sidebarView: string;
  modalView: string;
  userAvatar: string;
  notifications: INotification[];
  tags: TagEntity[];
  breadcrumbs: Breadcrumb[];
  configs: unknown;
  meta: any;
  commerce: Commerce;
  isConsentManagerInitialized: boolean;
  siteConfig: ISiteConfig | {};
}

const initialState = {
  displaySidebar: false,
  displayDropdown: false,
  displayModal: false,
  modalView: "LOGIN_VIEW",
  sidebarView: "CART_VIEW",
  userAvatar: "",
  notifications: [],
  tags: [],
  breadcrumbs: [],
  configs: {},
  meta: {},
  commerce: {},
  isConsentManagerInitialized: false,
  siteConfig: {},
};

type IAction =
  | {
      type: "OPEN_SIDEBAR";
    }
  | {
      type: "CLOSE_SIDEBAR";
    }
  | {
      type: "OPEN_DROPDOWN";
    }
  | {
      type: "CLOSE_DROPDOWN";
    }
  | {
      type: "OPEN_MODAL";
    }
  | {
      type: "CLOSE_MODAL";
    }
  | {
      type: "SET_MODAL_VIEW";
      view: MODAL_VIEWS;
    }
  | {
      type: "SET_SIDEBAR_VIEW";
      view: SIDEBAR_VIEWS;
    }
  | {
      type: "SET_USER_AVATAR";
      value: string;
    }
  | {
      type: "ADD_NOTIFICATION";
      value: INotification;
    }
  | {
      type: "SET_TAGS";
      value: TagEntity[];
    }
  | {
      type: "SET_BREADCRUMBS";
      value: Breadcrumb[];
    }
  | {
      type: "SET_META";
      value: any;
    }
  | {
      type: "SET_CONSENT_MANAGER_INITIALIZED";
      value: boolean;
    }
  | {
      type: "SET_COMMERCE_CONFIG";
      value: Commerce;
    };

type MODAL_VIEWS =
  | "SIGNUP_VIEW"
  | "LOGIN_VIEW"
  | "FORGOT_VIEW"
  | "NEW_SHIPPING_ADDRESS"
  | "NEW_PAYMENT_METHOD";

export enum SIDEBAR_VIEWS {
  CART_VIEW = "CART_VIEW",
  CHECKOUT_VIEW = "CHECKOUT_VIEW",
  PAYMENT_METHOD_VIEW = "PAYMENT_METHOD_VIEW",
  FILTER_VIEW = "FILTER_VIEW",
}

export const UIContext = createContext<IState | any>(initialState);
UIContext.displayName = "UIContext";

function uiReducer(state: IState, action: IAction) {
  switch (action.type) {
    case "OPEN_SIDEBAR": {
      return {
        ...state,
        displaySidebar: true,
      };
    }
    case "CLOSE_SIDEBAR": {
      return {
        ...state,
        displaySidebar: false,
      };
    }
    case "OPEN_DROPDOWN": {
      return {
        ...state,
        displayDropdown: true,
      };
    }
    case "CLOSE_DROPDOWN": {
      return {
        ...state,
        displayDropdown: false,
      };
    }
    case "OPEN_MODAL": {
      return {
        ...state,
        displayModal: true,
        displaySidebar: false,
      };
    }
    case "CLOSE_MODAL": {
      return {
        ...state,
        displayModal: false,
      };
    }
    case "SET_MODAL_VIEW": {
      return {
        ...state,
        modalView: action.view,
      };
    }
    case "SET_SIDEBAR_VIEW": {
      return {
        ...state,
        sidebarView: action.view,
      };
    }
    case "SET_USER_AVATAR": {
      return {
        ...state,
        userAvatar: action.value,
      };
    }
    case "ADD_NOTIFICATION": {
      return {
        ...state,
        notifications: [...state.notifications, action.value],
      };
    }
    case "SET_TAGS": {
      return {
        ...state,
        tags: action.value,
      };
    }
    case "SET_BREADCRUMBS": {
      return {
        ...state,
        breadcrumbs: action.value,
      };
    }
    case "SET_META": {
      return {
        ...state,
        meta: {
          ...state.meta,
          ...action.value,
        },
      };
    }
    case "SET_COMMERCE_CONFIG": {
      return {
        ...state,
        commerce: action.value,
      };
    }
    case "SET_CONSENT_MANAGER_INITIALIZED": {
      return {
        ...state,
        isConsentManagerInitialized: true,
      };
    }
  }
}

type Props = {
  children?: React.ReactNode;
  init?: Pick<IState, "tags" | "configs" | "siteConfig">;
};
export const UIContextProvider = ({
  children,
  init = { tags: [], configs: {}, siteConfig: {} },
  ...rest
}: Props) => {
  const [state, dispatch] = useReducer(uiReducer, {
    ...initialState,
    ...init,
  });

  const openSidebar = useCallback(() => {
    dispatch({ type: "OPEN_SIDEBAR" });
    EventBus.dispatch(Events.viewBasket);
  }, [dispatch]);
  const closeSidebar = useCallback(
    () => dispatch({ type: "CLOSE_SIDEBAR" }),
    [dispatch]
  );
  const toggleSidebar = useCallback(
    () =>
      state.displaySidebar
        ? dispatch({ type: "CLOSE_SIDEBAR" })
        : dispatch({ type: "OPEN_SIDEBAR" }),
    [dispatch, state.displaySidebar]
  );
  const closeSidebarIfPresent = useCallback(
    () => state.displaySidebar && dispatch({ type: "CLOSE_SIDEBAR" }),
    [dispatch, state.displaySidebar]
  );

  const openDropdown = useCallback(
    () => dispatch({ type: "OPEN_DROPDOWN" }),
    [dispatch]
  );
  const closeDropdown = useCallback(
    () => dispatch({ type: "CLOSE_DROPDOWN" }),
    [dispatch]
  );

  const openModal = useCallback(
    () => dispatch({ type: "OPEN_MODAL" }),
    [dispatch]
  );
  const closeModal = useCallback(
    () => dispatch({ type: "CLOSE_MODAL" }),
    [dispatch]
  );

  const setUserAvatar = useCallback(
    (value: string) => dispatch({ type: "SET_USER_AVATAR", value }),
    [dispatch]
  );

  const setModalView = useCallback(
    (view: MODAL_VIEWS) => dispatch({ type: "SET_MODAL_VIEW", view }),
    [dispatch]
  );

  const setSidebarView = useCallback(
    (view: SIDEBAR_VIEWS) => dispatch({ type: "SET_SIDEBAR_VIEW", view }),
    [dispatch]
  );

  const addNotification = useCallback(
    (value: INotification) => dispatch({ type: "ADD_NOTIFICATION", value }),
    [dispatch]
  );

  const setTags = useCallback(
    (value: TagEntity[]) => dispatch({ type: "SET_TAGS", value }),
    [dispatch]
  );

  const setBreadcrumbs = useCallback(
    (value: Breadcrumb[]) => dispatch({ type: "SET_BREADCRUMBS", value }),
    [dispatch]
  );

  const setMeta = useCallback(
    (value: any) => dispatch({ type: "SET_META", value }),
    [dispatch]
  );

  const setCommerceConfig = useCallback(
    (value: any) => dispatch({ type: "SET_COMMERCE_CONFIG", value }),
    [dispatch]
  );

  const setConsentManagerInitialized = useCallback(
    () => dispatch({ type: "SET_CONSENT_MANAGER_INITIALIZED", value: true }),
    [dispatch]
  );

  const value = useMemo(
    () => ({
      ...state,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      closeSidebarIfPresent,
      openDropdown,
      closeDropdown,
      openModal,
      closeModal,
      setModalView,
      setSidebarView,
      setUserAvatar,
      addNotification,
      setTags,
      setBreadcrumbs,
      setMeta,
      setCommerceConfig,
      setConsentManagerInitialized,
    }),
    [state]
  );

  return (
    <UIContext.Provider value={value} {...rest}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error(`useUI must be used within a UIProvider`);
  }
  return context;
};
